/**
 * Fleet Cost Reports Component
 * ============================
 * Comprehensive cost analysis and reporting for fleet managers.
 * Includes charts, breakdowns, and export functionality.
 */

import React, { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Download,
  Filter,
  Calendar,
  Truck,
  Building2,
  Wrench,
  BarChart3,
  PieChart,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RePieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
} from 'recharts';
import { fleetApi } from '../../services/fleetApi';

// Color palette for charts
const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#6366F1', '#14B8A6'];

// Date range presets
const DATE_PRESETS = [
  { label: 'This Month', value: 'this_month' },
  { label: 'Last Month', value: 'last_month' },
  { label: 'This Quarter', value: 'this_quarter' },
  { label: 'This Year', value: 'this_year' },
  { label: 'Last 12 Months', value: 'last_12_months' },
  { label: 'Custom', value: 'custom' },
];

// Get date range from preset
const getDateRange = (preset) => {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth();
  
  switch (preset) {
    case 'this_month':
      return {
        from: new Date(year, month, 1),
        to: new Date(year, month + 1, 0),
      };
    case 'last_month':
      return {
        from: new Date(year, month - 1, 1),
        to: new Date(year, month, 0),
      };
    case 'this_quarter':
      const quarterStart = Math.floor(month / 3) * 3;
      return {
        from: new Date(year, quarterStart, 1),
        to: new Date(year, quarterStart + 3, 0),
      };
    case 'this_year':
      return {
        from: new Date(year, 0, 1),
        to: new Date(year, 11, 31),
      };
    case 'last_12_months':
    default:
      return {
        from: new Date(year - 1, month, 1),
        to: today,
      };
  }
};

// Complete literal class strings per summary-card color role — Tailwind's
// scanner cannot see a runtime-assembled class like `bg-${color}-100`, so
// this lookup replaces that interpolation. `blue` and `purple` collapse to
// the neutral `info` family (static, non-interactive stat icons); `green`
// maps to the semantic `success` token; `orange` has no token in this
// design system, so it stays a literal (non-interpolated) raw class, which
// Tailwind's static scanner can still find and generate CSS for.
const SUMMARY_TONE = {
  blue: { bg: 'bg-info-tint', text: 'text-info' },
  green: { bg: 'bg-success-tint', text: 'text-success' },
  purple: { bg: 'bg-info-tint', text: 'text-info' },
  orange: { bg: 'bg-warning-tint', text: 'text-warning' },
};

// Summary Card
const SummaryCard = ({ title, value, change, changeLabel, icon: Icon, color }) => {
  const isPositive = change >= 0;
  const tone = SUMMARY_TONE[color] ?? SUMMARY_TONE.blue;

  return (
    <div className="bg-surface-card rounded-xl border border-border-default p-6">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-medium text-ink-muted">{title}</span>
        <div className={`p-2 rounded-lg ${tone.bg}`}>
          <Icon className={`w-5 h-5 ${tone.text}`} />
        </div>
      </div>
      <p className="text-3xl font-bold text-ink-soft">{value}</p>
      {change !== undefined && (
        <div className={`flex items-center mt-2 text-sm ${isPositive ? 'text-danger' : 'text-success'}`}>
          {isPositive ? <TrendingUp className="w-4 h-4 mr-1" /> : <TrendingDown className="w-4 h-4 mr-1" />}
          <span>{Math.abs(change).toFixed(1)}% {changeLabel}</span>
        </div>
      )}
    </div>
  );
};

// Report Section Wrapper
const ReportSection = ({ title, children, actions }) => (
  <div className="bg-surface-card rounded-xl border border-border-default overflow-hidden">
    <div className="px-6 py-4 border-b border-border-default flex items-center justify-between">
      <h3 className="text-lg font-semibold text-ink-soft">{title}</h3>
      {actions}
    </div>
    <div className="p-6">{children}</div>
  </div>
);

// Main Component
const FleetCostReports = () => {
  const { fleetId } = useParams();
  
  // State
  const [datePreset, setDatePreset] = useState('last_12_months');
  const [customDateRange, setCustomDateRange] = useState({ from: '', to: '' });
  const [activeView, setActiveView] = useState('overview');

  // Calculate date range
  const dateRange = useMemo(() => {
    if (datePreset === 'custom' && customDateRange.from && customDateRange.to) {
      return {
        from: new Date(customDateRange.from),
        to: new Date(customDateRange.to),
      };
    }
    return getDateRange(datePreset);
  }, [datePreset, customDateRange]);

  const formatDate = (date) => date.toISOString().split('T')[0];

  // Fetch reports
  const { data: byVehicle, isLoading: loadingByVehicle } = useQuery({
    queryKey: ['costReport', fleetId, 'by_vehicle', dateRange],
    queryFn: () => fleetApi.getCostReport(fleetId, {
      report_type: 'by_vehicle',
      date_from: formatDate(dateRange.from),
      date_to: formatDate(dateRange.to),
    }),
  });

  const { data: byDepartment, isLoading: loadingByDept } = useQuery({
    queryKey: ['costReport', fleetId, 'by_department', dateRange],
    queryFn: () => fleetApi.getCostReport(fleetId, {
      report_type: 'by_department',
      date_from: formatDate(dateRange.from),
      date_to: formatDate(dateRange.to),
    }),
  });

  const { data: byMonth, isLoading: loadingByMonth } = useQuery({
    queryKey: ['costReport', fleetId, 'by_month', dateRange],
    queryFn: () => fleetApi.getCostReport(fleetId, {
      report_type: 'by_month',
      date_from: formatDate(dateRange.from),
      date_to: formatDate(dateRange.to),
    }),
  });

  const { data: byServiceType, isLoading: loadingByType } = useQuery({
    queryKey: ['costReport', fleetId, 'by_service_type', dateRange],
    queryFn: () => fleetApi.getCostReport(fleetId, {
      report_type: 'by_service_type',
      date_from: formatDate(dateRange.from),
      date_to: formatDate(dateRange.to),
    }),
  });

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  // Calculate summary stats
  const summary = useMemo(() => {
    const totalCost = byMonth?.grand_total || 0;
    const totalServices = byMonth?.items?.reduce((sum, i) => sum + i.service_count, 0) || 0;
    const avgPerService = totalServices > 0 ? totalCost / totalServices : 0;
    
    // Calculate month-over-month change (simplified)
    const items = byMonth?.items || [];
    const lastMonth = items[items.length - 1]?.cost_total || 0;
    const prevMonth = items[items.length - 2]?.cost_total || 0;
    const change = prevMonth > 0 ? ((lastMonth - prevMonth) / prevMonth) * 100 : 0;

    return { totalCost, totalServices, avgPerService, change };
  }, [byMonth]);

  // Handle export
  const handleExport = async () => {
    try {
      const blob = await fleetApi.exportCostReport(fleetId, {
        date_from: formatDate(dateRange.from),
        date_to: formatDate(dateRange.to),
      });
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fleet-costs-${formatDate(dateRange.from)}-to-${formatDate(dateRange.to)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const isLoading = loadingByVehicle || loadingByDept || loadingByMonth || loadingByType;

  return (
    <div className="min-h-screen bg-surface-page">
      {/* Header */}
      <div className="bg-surface-card border-b border-border-default">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-ink-soft">Cost Reports</h1>
              <p className="mt-1 text-sm text-ink-muted">
                Analyze maintenance costs across your fleet
              </p>
            </div>
            <button
              onClick={handleExport}
              className="inline-flex items-center px-4 py-2 border border-border-default text-sm font-medium rounded-lg text-ink-soft bg-surface-card hover:bg-surface-muted"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </button>
          </div>

          {/* Date Range Selector */}
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <div className="flex items-center bg-surface-muted rounded-lg p-1">
              {DATE_PRESETS.map((preset) => (
                <button
                  key={preset.value}
                  onClick={() => setDatePreset(preset.value)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    datePreset === preset.value
                      ? 'bg-surface-card text-ink-soft shadow-sm'
                      : 'text-ink-muted hover:text-ink-soft'
                  }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>

            {datePreset === 'custom' && (
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={customDateRange.from}
                  onChange={(e) => setCustomDateRange({ ...customDateRange, from: e.target.value })}
                  className="px-3 py-1.5 border border-border-strong rounded-lg text-sm"
                />
                <span className="text-ink-muted">to</span>
                <input
                  type="date"
                  value={customDateRange.to}
                  onChange={(e) => setCustomDateRange({ ...customDateRange, to: e.target.value })}
                  className="px-3 py-1.5 border border-border-strong rounded-lg text-sm"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent" />
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <SummaryCard
                title="Total Maintenance Cost"
                value={formatCurrency(summary.totalCost)}
                change={summary.change}
                changeLabel="vs prev. month"
                icon={DollarSign}
                color="blue"
              />
              <SummaryCard
                title="Total Services"
                value={summary.totalServices.toLocaleString()}
                icon={Wrench}
                color="green"
              />
              <SummaryCard
                title="Avg. Cost per Service"
                value={formatCurrency(summary.avgPerService)}
                icon={BarChart3}
                color="purple"
              />
              <SummaryCard
                title="Vehicles Serviced"
                value={byVehicle?.items?.length || 0}
                icon={Truck}
                color="orange"
              />
            </div>

            {/* Charts Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Monthly Trend */}
              <ReportSection title="Monthly Cost Trend">
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={byMonth?.items || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis 
                      tick={{ fontSize: 12 }} 
                      tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                    />
                    <Tooltip 
                      formatter={(value) => formatCurrency(value)}
                      labelStyle={{ fontWeight: 'bold' }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="cost_total" 
                      stroke="#3B82F6" 
                      strokeWidth={2}
                      dot={{ fill: '#3B82F6', strokeWidth: 2 }}
                      name="Total Cost"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </ReportSection>

              {/* Cost by Service Type */}
              <ReportSection title="Cost by Service Type">
                <ResponsiveContainer width="100%" height={300}>
                  <RePieChart>
                    <Pie
                      data={byServiceType?.items || []}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="cost_total"
                      nameKey="name"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {(byServiceType?.items || []).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                  </RePieChart>
                </ResponsiveContainer>
              </ReportSection>
            </div>

            {/* Cost by Vehicle */}
            <ReportSection 
              title="Cost by Vehicle"
              actions={
                <span className="text-sm text-ink-muted">
                  {byVehicle?.items?.length || 0} vehicles
                </span>
              }
            >
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={(byVehicle?.items || []).slice(0, 15)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis 
                    type="number" 
                    tick={{ fontSize: 12 }} 
                    tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                  />
                  <YAxis 
                    type="category" 
                    dataKey="name" 
                    tick={{ fontSize: 12 }} 
                    width={100}
                  />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Bar dataKey="cost_parts" stackId="a" fill="#3B82F6" name="Parts" />
                  <Bar dataKey="cost_labor" stackId="a" fill="#10B981" name="Labor" />
                  <Bar dataKey="cost_other" stackId="a" fill="#F59E0B" name="Other" />
                </BarChart>
              </ResponsiveContainer>
            </ReportSection>

            {/* Cost by Department */}
            <div className="mt-6">
              <ReportSection title="Cost by Department">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-border-default">
                    <thead>
                      <tr>
                        <th className="px-4 py-3 text-left text-small font-medium text-ink-muted uppercase tracking-wider">
                          Department
                        </th>
                        <th className="px-4 py-3 text-right text-small font-medium text-ink-muted uppercase tracking-wider">
                          Services
                        </th>
                        <th className="px-4 py-3 text-right text-small font-medium text-ink-muted uppercase tracking-wider">
                          Parts
                        </th>
                        <th className="px-4 py-3 text-right text-small font-medium text-ink-muted uppercase tracking-wider">
                          Labor
                        </th>
                        <th className="px-4 py-3 text-right text-small font-medium text-ink-muted uppercase tracking-wider">
                          Other
                        </th>
                        <th className="px-4 py-3 text-right text-small font-medium text-ink-muted uppercase tracking-wider">
                          Total
                        </th>
                        <th className="px-4 py-3 text-right text-small font-medium text-ink-muted uppercase tracking-wider">
                          % of Total
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border-default">
                      {(byDepartment?.items || []).map((dept, idx) => (
                        <tr key={idx} className="hover:bg-surface-muted">
                          <td className="px-4 py-4 text-sm font-medium text-ink-soft">
                            <div className="flex items-center">
                              <Building2 className="w-4 h-4 text-ink-muted mr-2" />
                              {dept.name}
                            </div>
                          </td>
                          <td className="px-4 py-4 text-sm text-ink-muted text-right">
                            {dept.service_count}
                          </td>
                          <td className="px-4 py-4 text-sm text-ink-muted text-right">
                            {formatCurrency(dept.cost_parts)}
                          </td>
                          <td className="px-4 py-4 text-sm text-ink-muted text-right">
                            {formatCurrency(dept.cost_labor)}
                          </td>
                          <td className="px-4 py-4 text-sm text-ink-muted text-right">
                            {formatCurrency(dept.cost_other)}
                          </td>
                          <td className="px-4 py-4 text-sm font-semibold text-ink-soft text-right">
                            {formatCurrency(dept.cost_total)}
                          </td>
                          <td className="px-4 py-4 text-sm text-ink-muted text-right">
                            {byDepartment?.grand_total > 0 
                              ? ((dept.cost_total / byDepartment.grand_total) * 100).toFixed(1)
                              : 0}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-surface-page">
                      <tr>
                        <td className="px-4 py-4 text-sm font-bold text-ink-soft">Total</td>
                        <td className="px-4 py-4 text-sm font-bold text-ink-soft text-right">
                          {(byDepartment?.items || []).reduce((sum, d) => sum + d.service_count, 0)}
                        </td>
                        <td className="px-4 py-4 text-sm font-bold text-ink-soft text-right">
                          {formatCurrency((byDepartment?.items || []).reduce((sum, d) => sum + parseFloat(d.cost_parts || 0), 0))}
                        </td>
                        <td className="px-4 py-4 text-sm font-bold text-ink-soft text-right">
                          {formatCurrency((byDepartment?.items || []).reduce((sum, d) => sum + parseFloat(d.cost_labor || 0), 0))}
                        </td>
                        <td className="px-4 py-4 text-sm font-bold text-ink-soft text-right">
                          {formatCurrency((byDepartment?.items || []).reduce((sum, d) => sum + parseFloat(d.cost_other || 0), 0))}
                        </td>
                        <td className="px-4 py-4 text-sm font-bold text-ink-soft text-right">
                          {formatCurrency(byDepartment?.grand_total)}
                        </td>
                        <td className="px-4 py-4 text-sm font-bold text-ink-soft text-right">100%</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </ReportSection>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FleetCostReports;
